import { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  isScrolled?: boolean;
}

export default function Navbar({ isScrolled = false }: NavbarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { 
      name: 'Services', 
      path: '#',
      submenu: [
        { name: 'Security Systems', path: '/services/security-systems' },
        { name: 'Fire Safety Solutions', path: '/services/fire-safety' },
        { name: 'Risk Assessment', path: '/services/risk-assessment' },
        { name: 'Compliance Solutions', path: '/services/compliance' },
        { name: 'Maintenance & Support', path: '/services/maintenance' },
        { name: 'Consultancy Services', path: '/services/consultancy' },
        { name: 'Security & Event Operations', path: '/services/security-event-operations' }
      ]
    },
    { name: 'Training & Certification', path: '/training' },
    { name: 'Courses', path: '/courses' },
    { name: 'Certifications', path: '/certifications' }
  ];

  const scrollToSection = (sectionId: string) => {
    if (!isHomePage) {
      window.location.href = `/#${sectionId}`;
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setShowServicesDropdown(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setShowServicesDropdown(false);
    }, 300);
  };

  return (
    <nav className="fixed w-full z-50 transition-all duration-300 bg-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <a href="/" className="flex items-center space-x-3 cursor-pointer">
            <img 
              src="https://static.readdy.ai/image/74d1e4e79521c370342183571976e552/640721aa39941874b11ee1cb82c47c85.png" 
              alt="JL Security Logo" 
              className="h-12 w-auto"
            />
            <div>
              <div className="text-xl font-bold font-poppins text-gray-900">
                JL Security
              </div>
              <div className="text-xs font-inter text-gray-600">
                Security, Risk & Compliance
              </div>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('home')}
              className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-red-600"
            >
              Home
            </button>
            
            {/* Services Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button className="text-gray-700 hover:text-red-600 transition-colors duration-300 font-medium flex items-center whitespace-nowrap cursor-pointer">
                Services
                <i className="ri-arrow-down-s-line ml-1"></i>
              </button>
              
              {showServicesDropdown && (
                <div 
                  className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link 
                    to="/services/security-systems" 
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-200 cursor-pointer"
                  >
                    <i className="ri-shield-check-line mr-2"></i>
                    Security Systems
                  </Link>
                  <Link 
                    to="/services/fire-safety" 
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-200 cursor-pointer"
                  >
                    <i className="ri-fire-line mr-2"></i>
                    Fire Safety Solutions
                  </Link>
                  <Link 
                    to="/services/risk-assessment" 
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-200 cursor-pointer"
                  >
                    <i className="ri-file-shield-2-line mr-2"></i>
                    Risk Assessment
                  </Link>
                  <Link 
                    to="/services/compliance" 
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-200 cursor-pointer"
                  >
                    <i className="ri-checkbox-circle-line mr-2"></i>
                    Compliance Solutions
                  </Link>
                  <Link 
                    to="/services/maintenance" 
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-200 cursor-pointer"
                  >
                    <i className="ri-tools-line mr-2"></i>
                    Maintenance & Support
                  </Link>
                  <Link 
                    to="/services/consultancy" 
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-200 cursor-pointer"
                  >
                    <i className="ri-user-settings-line mr-2"></i>
                    Consultancy Services
                  </Link>
                  <Link 
                    to="/services/security-event-operations" 
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-200 cursor-pointer"
                  >
                    <i className="ri-shield-user-line mr-2"></i>
                    Security & Event Operations
                  </Link>
                </div>
              )}
            </div>
            
            <Link
              to="/about"
              className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-red-600"
            >
              About Us
            </Link>
            <Link
              to="/courses"
              className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-red-600"
            >
              Courses
            </Link>
            <Link
              to="/certifications"
              className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-red-600"
            >
              Certifications
            </Link>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-red-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg cursor-pointer text-gray-700"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-3 space-y-3">
            <button
              onClick={() => scrollToSection('home')}
              className="block w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer"
            >
              Home
            </button>
            
            {/* Mobile Services Dropdown */}
            <div>
              <button
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className="flex items-center justify-between w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer"
              >
                Services
                <i className={`ri-arrow-${isServicesOpen ? 'up' : 'down'}-s-line text-lg`}></i>
              </button>
              {isServicesOpen && (
                <div className="ml-4 mt-2 space-y-2">
                  <Link
                    to="/services/security-systems"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg cursor-pointer"
                  >
                    <i className="ri-shield-check-line mr-2"></i>
                    Security Systems
                  </Link>
                  <Link
                    to="/services/fire-safety"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg cursor-pointer"
                  >
                    <i className="ri-fire-line mr-2"></i>
                    Fire Safety Solutions
                  </Link>
                  <Link
                    to="/services/risk-assessment"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg cursor-pointer"
                  >
                    <i className="ri-file-shield-2-line mr-2"></i>
                    Risk Assessment
                  </Link>
                  <Link
                    to="/services/compliance"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg cursor-pointer"
                  >
                    <i className="ri-checkbox-circle-line mr-2"></i>
                    Compliance Solutions
                  </Link>
                  <Link
                    to="/services/maintenance"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg cursor-pointer"
                  >
                    <i className="ri-tools-line mr-2"></i>
                    Maintenance & Support
                  </Link>
                  <Link
                    to="/services/consultancy"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg cursor-pointer"
                  >
                    <i className="ri-user-settings-line mr-2"></i>
                    Consultancy Services
                  </Link>
                  <Link
                    to="/services/security-event-operations"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg cursor-pointer"
                  >
                    <i className="ri-shield-user-line mr-2"></i>
                    Security & Event Operations
                  </Link>
                </div>
              )}
            </div>
            
            <Link
              to="/about"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer"
            >
              About Us
            </Link>
            <Link
              to="/courses"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer"
            >
              Courses
            </Link>
            <Link
              to="/certifications"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer"
            >
              Certifications
            </Link>
            <button
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left px-4 py-2 text-sm font-medium bg-red-600 text-white hover:bg-red-700 rounded-lg cursor-pointer"
            >
              Contact
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
